import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { useAgent } from '../../context/AgentContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Clock, Bell, Maximize2, Minimize2 } from 'lucide-react';

// Pages where the global agent picker is shown
const AGENT_PATHS = ['/vip-dashboard', '/player-trajectory', '/contact-tracker'];

function UtcClock() {
  const [time, setTime] = useState(() => new Date().toUTCString().slice(17, 25));
  useEffect(() => {
    const id = setInterval(() => setTime(new Date().toUTCString().slice(17, 25)), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-dim tabular-nums">
      <Clock className="h-3 w-3" />
      {time}
      <span className="text-[10px] opacity-50">UTC</span>
    </span>
  );
}

function NotificationBellButton() {
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={() => navigate('/notifications')}
          className="relative p-1.5 rounded-md text-ink-dim hover:text-ink hover:bg-surface-2 transition-colors"
        >
          <Bell className={`h-4 w-4 ${unreadCount > 0 ? 'text-brand-yellow' : ''}`} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] rounded-full bg-brand-red text-white flex items-center justify-center px-[3px] text-[9px] font-bold leading-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent>Notifications{unreadCount > 0 ? ` (${unreadCount})` : ''}</TooltipContent>
    </Tooltip>
  );
}

function AgentSelector() {
  const { agents, selectedAgent, setSelectedAgent } = useAgent();

  if (agents.length === 0) return null;

  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-ink-dim">VIP manager</span>
      <Select value={selectedAgent} onValueChange={setSelectedAgent}>
        <SelectTrigger className="h-7 w-[160px] text-xs">
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {agents.map(a => (
            <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Toggles full-width mode: every page's content uses the whole screen width
// instead of its usual cap.
function FullWidthButton({ fullWidth, onToggle }) {
  const Icon = fullWidth ? Minimize2 : Maximize2;
  const label = fullWidth ? 'Normal width' : 'Full width';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          aria-label={label}
          aria-pressed={fullWidth}
          className="h-7 w-7 rounded-md"
        >
          <Icon className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export default function TopBar({ fullWidth = false, onToggleFullWidth }) {
  const location = useLocation();
  const showAgentSelector = AGENT_PATHS.some(p => location.pathname === p);

  return (
    <header className="sticky top-0 z-30 flex items-center h-14 px-6 border-b border-white/10 bg-surface-1/80 backdrop-blur-sm">
      <div className="flex-1 flex items-center gap-3">
        <h1 className="font-display text-sm tracking-tight text-ink-dim">
          VIP Tools
        </h1>
        {showAgentSelector && (
          <>
            <Separator orientation="vertical" className="h-5 bg-white/10" />
            <AgentSelector />
          </>
        )}
      </div>
      <UtcClock />
      <Separator orientation="vertical" className="mx-4 h-5 bg-white/10" />
      {onToggleFullWidth && (
        <>
          <FullWidthButton fullWidth={fullWidth} onToggle={onToggleFullWidth} />
          <Separator orientation="vertical" className="mx-4 h-5 bg-white/10" />
        </>
      )}
      <NotificationBellButton />
    </header>
  );
}
